/*
  ================================================================
  CustomerInvitations.jsx
  Customer Invitation Management Module — All 4 Screens
  Screens: LIST → CREATE (4 steps) → PREVIEW → RSVP TRACKING
  Prefix: cim- (on all state vars, class names, functions)
  ✅ CustomerLayout properly wrapped on all screens
  ================================================================
*/
import React, { useState, useMemo, useEffect, useContext } from 'react';
import '../../../../assets/css/customer/dashboard/Invitations.css';
import CustomerLayout from '../../../../layouts/customer/CustomerLayout';
import AppContext from '../../../../context/AppContext';
import axios from 'axios';

/* ─────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────── */
const CIM_PER_PAGE    = 6;
const CIM_EVENT_TYPES = ['All', 'Wedding', 'Mehndi', 'Barat', 'Walima'];

const CIM_MOCK_EVENTS = [
  { id: 'BK-101', label: 'Wedding Booking #BK-101',   type: 'Wedding', icon: '💍' },
  { id: 'BK-102', label: 'Mehndi Booking #BK-102',    type: 'Mehndi',  icon: '🌿' },
  { id: 'BK-103', label: 'Barat Booking #BK-103',     type: 'Barat',   icon: '🎊' },
  { id: 'BK-104', label: 'Walima Booking #BK-104',    type: 'Walima',  icon: '🍽️' },
];

// Sirf list-screen ke icon lookup ke liye rakha hai (purane invitations ke templateId se emoji dikhane ke liye)
const CIM_TEMPLATES = [
  { id: 't1', name: 'Royal Gold',     theme: 'royal',   emoji: '👑' },
  { id: 't2', name: 'Floral Pink',    theme: 'floral',  emoji: '🌸' },
  { id: 't3', name: 'Elegant White',  theme: 'minimal', emoji: '🕊️' },
  { id: 't4', name: 'Classic Cream',  theme: 'classic', emoji: '🌟' },
  { id: 't5', name: 'Modern Dark',    theme: 'modern',  emoji: '🖤' },
];

const CIM_EMPTY_FORM = {
  eventId: '', eventType: '', eventLabel: '',
  templateId: '', templateName: '', templateTheme: '',
  realEventId: '',
  brideName: '', groomName: '', eventDate: '', venue: '', customMessage: '',
};

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */
const cimEventEmoji = (type) =>
  ({ Wedding: '💍', Mehndi: '🌿', Barat: '🎊', Walima: '🍽️' }[type] || '✨');

const cimEventBadgeClass = (type) => {
  const m = { Wedding:'--wedding', Mehndi:'--mehndi', Barat:'--barat', Walima:'--walima' };
  return `cim-event-badge cim-event-badge${m[type] || '--other'}`;
};

const cimStatusBadgeClass = (s) =>
  `cim-status-badge cim-status-badge--${(s || '').toLowerCase()}`;

const cimThumbClass = (theme) =>
  `cim-template-thumb cim-template-thumb--${theme || 'default'}`;

const cimInitials = (name) =>
  name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';

// Real (admin-created) template ke theme ke hisaab se emoji dikhane ke liye
const cimThemeEmoji = (theme) =>
  ({ royal: '👑', floral: '🌸', minimal: '🕊️', classic: '🌟', modern: '🖤' }[(theme || '').toLowerCase()] || '✉️');

/* ─────────────────────────────────────────────
   TOAST
───────────────────────────────────────────── */
const CimToast = ({ msg, type }) => (
  <div className={`cim-toast cim-toast--${type}`}>
    <span>{type === 'success' ? '✅' : type === 'error' ? '❌' : '💌'}</span>
    {msg}
  </div>
);

/* ─────────────────────────────────────────────
   INVITATION CARD RENDERER
───────────────────────────────────────────── */
const CimInvCard = ({ inv, compact = false }) => {
  const isDark = inv.templateTheme === 'modern' || inv.templateTheme === 'royal';
  const textClr = isDark ? '#fff' : '#1c1917';
  const bodyClr = isDark ? 'rgba(255,255,255,0.82)' : 'rgba(28,25,23,0.78)';
  const dimClr  = isDark ? 'rgba(255,255,255,0.55)' : 'rgba(28,25,23,0.5)';

  return (
    <div className={`cim-inv-card cim-inv-card--${inv.templateTheme || 'classic'}`}>
      <p className="cim-inv-bismillah" style={{ color: dimClr }}>
        Bismillah-ir-Rahman-ir-Raheem
      </p>
      <span className="cim-inv-ornament">〰 ✦ 〰</span>
      <h3 className="cim-inv-header" style={{ color: textClr }}>
        You are cordially invited to the<br />{inv.eventType} Ceremony of
      </h3>
      <div className="cim-inv-couple" style={{ color: textClr }}>
        {inv.groomName || 'Groom'} &amp; {inv.brideName || 'Bride'}
      </div>
      <div className="cim-inv-divider" style={{ background: textClr }} />
      <p className="cim-inv-body" style={{ color: bodyClr }}>
        📅 {inv.eventDate || '___________'}<br />
        📍 {inv.venue     || '___________'}
        {inv.customMessage && (<><br /><br /><em>{inv.customMessage}</em></>)}
      </p>
      <div className="cim-inv-divider" style={{ background: textClr }} />
      <p className="cim-inv-footer" style={{ color: dimClr }}>
        ✦ {inv.templateName} ✦
      </p>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════ */
const Invitations = () => {

  /* ── Customer context (logged-in customer ka id + baseUrl) ── */
  const { baseUrl, baseUrlForFile, loggedInCustomer, regexChecker } = useContext(AppContext);
  const CIM_API_BASE = `${baseUrl}/customer/invitation`;
  const CIM_TEMPLATES_API = `${baseUrl}/admin/invitation-templates`;
  const CIM_EVENTS_API = `${baseUrl}/customer/events/viewAllEvents`;

  /* ── Screen state ── */
  const [cimScreen,      setCimScreen]      = useState('list');
  const [cimData,        setCimData]        = useState([]);      // Step 1: state banaya
  const [cimLoading,     setCimLoading]     = useState(true);

  /* List filters */
  const [cimSearch,      setCimSearch]      = useState('');
  const [cimFltEvent,    setCimFltEvent]    = useState('All');
  const [cimFltStatus,   setCimFltStatus]   = useState('All');
  const [cimPage,        setCimPage]        = useState(1);

  /* Create / Edit */
  const [cimStep,        setCimStep]        = useState(1);
  const [cimForm,        setCimForm]        = useState(CIM_EMPTY_FORM);
  const [cimGuests,      setCimGuests]      = useState([]);
  const [cimEditId,      setCimEditId]      = useState(null);

  /* Step 2: Real templates (admin ke banaye hue) */
  const [cimTemplates,        setCimTemplates]        = useState([]);
  const [cimTemplatesLoading, setCimTemplatesLoading]  = useState(false);

  /* Step 3: Customer ke real events (My Events se) */
  const [cimRealEvents,        setCimRealEvents]        = useState([]);
  const [cimRealEventsLoading, setCimRealEventsLoading]  = useState(false);

  /* Step 3: Venue field validation error (regexChecker based, jaisa CustomerProfile mein) */
  const [cimVenueError,  setCimVenueError]  = useState('');

  /* Step 4: Guest rows validation errors — { [guestId]: { name, email, phone } } */
  const [cimGuestErrors, setCimGuestErrors] = useState({});

  /* View */
  const [cimViewInv,     setCimViewInv]     = useState(null);

  /* Modals */
  const [cimDeleteId,    setCimDeleteId]    = useState(null);

  /* Toast */
  const [cimToast,       setCimToast]       = useState(null);

  /* ── Map backend (snake_case) response → frontend (camelCase) shape ── */
  const cimMapInvitation = (inv) => ({
    id: 'INV-' + inv.id,
    dbId: inv.id,
    eventId: inv.event_id, eventType: inv.event_type, eventLabel: inv.event_label,
    templateId: inv.template_id, templateName: inv.template_name, templateTheme: inv.template_theme,
    brideName: inv.bride_name, groomName: inv.groom_name,
    eventDate: inv.event_date, venue: inv.venue, customMessage: inv.custom_message,
    status: inv.status, totalSent: inv.total_sent,
    createdAt: inv.created_at,
    guests: (inv.guests || []).map(g => ({
      id: g.id, name: g.name, email: g.email, phone: g.phone,
    })),
  });

  /* Step 5: API call banai — Step 6: .then/.catch lagaya */
  const cimFetchInvitations = () => {
    setCimLoading(true);
    axios.get(`${CIM_API_BASE}/getinvitations`, { params: { customer_id: loggedInCustomer?.id } })
      .then(res => {
        const data = res.data;
        if (data.success) {
          setCimData(data.invitations.map(cimMapInvitation));
        } else {
          cimShowToast(data.message || 'Failed to load invitations.', 'error');
        }
        setCimLoading(false);
      })
      .catch(() => {
        cimShowToast('Server error while loading invitations.', 'error');
        setCimLoading(false);
      });
  };

  /* Page load pe invitations fetch karwana */
  useEffect(() => {
    if (loggedInCustomer?.id) cimFetchInvitations();
  }, [loggedInCustomer]);

  /* ── Step 5: Admin ke banaye hue templates fetch karna (event type + Active status se filter) ── */
  const cimFetchTemplates = (eventType) => {
    setCimTemplatesLoading(true);
    axios.get(CIM_TEMPLATES_API, { params: { event_type: eventType, status: 'Active' } })
      .then(res => {
        const data = res.data;
        if (data.success) {
          setCimTemplates(data.data);
        } else {
          setCimTemplates([]);
          cimShowToast(data.message || 'Failed to load templates.', 'error');
        }
        setCimTemplatesLoading(false);
      })
      .catch(() => {
        setCimTemplates([]);
        cimShowToast('Server error while loading templates.', 'error');
        setCimTemplatesLoading(false);
      });
  };

  /* Step 2 par pohanchte hi (aur event type maloom hone par) templates load karna */
  useEffect(() => {
    if (cimScreen === 'create' && cimStep === 2 && cimForm.eventType) {
      cimFetchTemplates(cimForm.eventType);
    }
  }, [cimScreen, cimStep, cimForm.eventType]);

  /* ── Step 3: Customer ke real events (My Events se) fetch karna ── */
  const cimFetchRealEvents = () => {
    setCimRealEventsLoading(true);
    const fd = new FormData();
    fd.append('wecustomer_id', loggedInCustomer?.id);

    axios.post(CIM_EVENTS_API, fd)
      .then(res => {
        setCimRealEvents(Array.isArray(res.data) ? res.data : []);
        setCimRealEventsLoading(false);
      })
      .catch(() => {
        setCimRealEvents([]);
        setCimRealEventsLoading(false);
      });
  };

  /* Step 3 par pohanchte hi real events load karna */
  useEffect(() => {
    if (cimScreen === 'create' && cimStep === 3) {
      cimFetchRealEvents();
    }
  }, [cimScreen, cimStep]);

  /* Real event select karne par bride/groom/date auto-fill karna */
  const cimSelectRealEvent = (ev) => {
    setCimForm(f => ({
      ...f,
      realEventId: ev.id,
      brideName: ev.brideName || '',
      groomName: ev.groomName || '',
      eventDate: ev.eventDate || '',
    }));
  };

  /* ── Computed stats ── */
  const cimStats = useMemo(() => {
    const total  = cimData.length;
    const totalG = cimData.reduce((a, i) => a + i.totalSent, 0);
    return { total, totalG };
  }, [cimData]);

  /* ── Filtered list ── */
  const cimFiltered = useMemo(() => cimData.filter(inv => {
    const ms = inv.id.toLowerCase().includes(cimSearch.toLowerCase()) ||
               inv.brideName.toLowerCase().includes(cimSearch.toLowerCase()) ||
               inv.groomName.toLowerCase().includes(cimSearch.toLowerCase());
    const me = cimFltEvent  === 'All' || inv.eventType === cimFltEvent;
    const mk = cimFltStatus === 'All' || inv.status    === cimFltStatus;
    return ms && me && mk;
  }), [cimData, cimSearch, cimFltEvent, cimFltStatus]);

  const cimTotalPages = Math.ceil(cimFiltered.length / CIM_PER_PAGE) || 1;
  const cimPaginated  = cimFiltered.slice(
    (cimPage - 1) * CIM_PER_PAGE,
    cimPage * CIM_PER_PAGE
  );

  /* ── Toast helper ── */
  const cimShowToast = (msg, type = 'success') => {
    setCimToast({ msg, type });
    setTimeout(() => setCimToast(null), 2800);
  };

  /* ── Open Create ── */
  const cimOpenCreate = () => {
    setCimEditId(null);
    setCimForm(CIM_EMPTY_FORM);
    setCimGuests([{ id: Date.now(), name: '', email: '', phone: '' }]);
    setCimStep(1);
    setCimVenueError('');
    setCimGuestErrors({});
    setCimScreen('create');
    window.scrollTo(0, 0);
  };

  /* ── Open Edit ── */
  const cimOpenEdit = (inv) => {
    setCimEditId(inv.id);
    setCimForm({
      eventId:       inv.eventId,
      eventType:     inv.eventType,
      eventLabel:    inv.eventLabel,
      templateId:    inv.templateId,
      templateName:  inv.templateName,
      templateTheme: inv.templateTheme,
      realEventId:   inv.realEventId || '',
      brideName:     inv.brideName,
      groomName:     inv.groomName,
      eventDate:     inv.eventDate,
      venue:         inv.venue,
      customMessage: inv.customMessage,
    });
    setCimGuests(inv.guests.map(g => ({ ...g })));
    setCimStep(1);
    setCimVenueError('');
    setCimGuestErrors({});
    setCimScreen('create');
    window.scrollTo(0, 0);
  };

  /* ── Step validation (required fields + regexChecker format checks) ── */
  const cimStepValid = () => {
    if (cimStep === 1) return !!cimForm.eventId;
    if (cimStep === 2) return !!cimForm.templateId;
    if (cimStep === 3) {
      const venueErr = cimForm.venue.trim() ? regexChecker('venue', cimForm.venue) : 'Venue is required';
      if (venueErr !== cimVenueError) setCimVenueError(venueErr);
      return !!cimForm.realEventId && !venueErr;
    }
    if (cimStep === 4) return !cimHasGuestErrors();
    return true;
  };

  /* ── Check if any filled guest row has an invalid email/phone format ── */
  const cimHasGuestErrors = () => {
    return cimGuests.some(g => {
      if (!g.name.trim()) return false; // empty row, ignore
      const emailErr = g.email ? regexChecker('email', g.email) : '';
      const phoneErr = g.phone ? regexChecker('phoneNumber', g.phone) : '';
      return !!emailErr || !!phoneErr;
    });
  };

  const cimGoNext = () => {
    if (!cimStepValid()) { cimShowToast('Please complete this step first!', 'error'); return; }
    if (cimStep < 4) setCimStep(s => s + 1);
    else cimGoToPreview();
  };

  /* ── Navigate to preview ── */
  const cimGoToPreview = () => {
    if (cimHasGuestErrors()) {
      cimShowToast('Please fix invalid guest email/phone entries first!', 'error');
      return;
    }
    setCimViewInv({
      ...(cimEditId ? cimData.find(i => i.id === cimEditId) : {}),
      ...cimForm,
      guests: cimGuests.filter(g => g.name.trim()),
      totalSent: cimGuests.filter(g => g.name.trim()).length,
    });
    setCimScreen('preview');
    window.scrollTo(0, 0);
  };

  /* ── Save as Draft ── */
  const cimSaveDraft = () => cimSaveInvitation('Draft');

  /* ── Confirm & Send ── */
  const cimSend = () => cimSaveInvitation('Sent');

  /* ── Core save — Step 4: FormData banai, Step 5: API call, Step 6: .then/.catch ── */
  const cimSaveInvitation = (status) => {
    if (cimHasGuestErrors()) {
      cimShowToast('Please fix invalid guest email/phone entries first!', 'error');
      return;
    }

    const filledGuests = cimGuests
      .filter(g => g.name.trim())
      .map(g => ({ name: g.name, email: g.email, phone: g.phone }));

    const fd = new FormData();
    fd.append('customer_id',    loggedInCustomer?.id);
    fd.append('event_id',       cimForm.eventId);
    fd.append('event_type',     cimForm.eventType);
    fd.append('event_label',    cimForm.eventLabel);
    fd.append('template_id',    cimForm.templateId);
    fd.append('template_name',  cimForm.templateName);
    fd.append('template_theme', cimForm.templateTheme);
    fd.append('bride_name',     cimForm.brideName);
    fd.append('groom_name',     cimForm.groomName);
    fd.append('event_date',     cimForm.eventDate);
    fd.append('venue',          cimForm.venue);
    fd.append('custom_message', cimForm.customMessage);
    fd.append('status',         status);
    fd.append('guests',         JSON.stringify(filledGuests));

    const editingInv = cimEditId ? cimData.find(i => i.id === cimEditId) : null;
    const url = editingInv
      ? `${CIM_API_BASE}/updateinvitation/${editingInv.dbId}`
      : `${CIM_API_BASE}/addinvitation`;

    axios.post(url, fd)
      .then(res => {
        const data = res.data;
        if (data.success) {
          cimFetchInvitations();
          setCimScreen('list');
          setCimPage(1);
          cimShowToast(data.message || 'Saved successfully!');
        } else {
          cimShowToast(data.message || 'Failed to save invitation.', 'error');
        }
      })
      .catch(() => cimShowToast('Server error while saving invitation.', 'error'));
  };

  /* ── Delete — Step 5: API call, Step 6: .then/.catch ── */
  const cimConfirmDelete = () => {
    const target = cimData.find(i => i.id === cimDeleteId);
    if (!target) return;

    axios.post(`${CIM_API_BASE}/deleteinvitation/${target.dbId}`)
      .then(res => {
        const data = res.data;
        if (data.success) {
          setCimData(prev => prev.filter(i => i.id !== cimDeleteId));
          setCimDeleteId(null);
          if (cimScreen !== 'list') setCimScreen('list');
          cimShowToast('Invitation deleted.', 'error');
        } else {
          cimShowToast(data.message || 'Failed to delete invitation.', 'error');
        }
      })
      .catch(() => cimShowToast('Server error while deleting invitation.', 'error'));
  };

  /* ── Guest list helpers ── */
  const cimAddGuest = () =>
    setCimGuests(prev => [...prev, { id: Date.now(), name: '', email: '', phone: '' }]);

  const cimUpdateGuest = (id, field, val) => {
    setCimGuests(prev => prev.map(g => g.id === id ? { ...g, [field]: val } : g));

    if (field === 'email' || field === 'phone') {
      const errType = field === 'email' ? 'email' : 'phoneNumber';
      const err = val ? regexChecker(errType, val) : '';
      setCimGuestErrors(prev => ({
        ...prev,
        [id]: { ...prev[id], [field]: err }
      }));
    }
  };

  const cimRemoveGuest = (id) => {
    setCimGuests(prev => prev.filter(g => g.id !== id));
    setCimGuestErrors(prev => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  /* ── CSV upload ── */
  const cimHandleCsv = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const lines = ev.target.result.split('\n').filter(Boolean);
      const newGuests = lines.slice(1).map((line, i) => {
        const parts = line.split(',');
        return { id: Date.now() + i, name: parts[0]?.trim() || '', email: parts[1]?.trim() || '', phone: parts[2]?.trim() || '' };
      }).filter(g => g.name);
      setCimGuests(prev => [...prev, ...newGuests]);
      cimShowToast(`${newGuests.length} guests imported!`);
    };
    reader.readAsText(file);
  };

  /* ── Delete target ── */
  const cimDeleteTarget = cimData.find(i => i.id === cimDeleteId);

  /* ═══════════════════════════════════════════
     STEP INDICATOR COMPONENT
  ═══════════════════════════════════════════ */
  const CimStepper = () => {
    const steps = [
      { num: 1, label: 'Select Event'    },
      { num: 2, label: 'Choose Template' },
      { num: 3, label: 'Fill Details'    },
      { num: 4, label: 'Guest List'      },
    ];
    return (
      <div className="cim-stepper">
        {steps.map((s, i) => (
          <React.Fragment key={s.num}>
            <div className={`cim-step ${cimStep === s.num ? 'cim-step--active' : ''} ${cimStep > s.num ? 'cim-step--done' : ''}`}>
              <div className="cim-step-circle">
                {cimStep > s.num ? '✓' : s.num}
              </div>
              <div className="cim-step-label">
                <span className="cim-step-num">Step {s.num}</span>
                <span className="cim-step-name">{s.label}</span>
              </div>
            </div>
            {i < steps.length - 1 && (
              <div className={`cim-step-connector ${cimStep > s.num ? 'cim-step-connector--done' : ''}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  /* ═══════════════════════════════════════════════
     SCREEN: LIST
  ═══════════════════════════════════════════════ */
  if (cimScreen === 'list') return (
    <CustomerLayout>
    <div className="cim-wrapper">

      {/* Header */}
      <div className="cim-page-header">
        <div className="cim-page-header-left">
          <div className="cim-page-icon">💌</div>
          <div>
            <h1 className="cim-page-title">My Invitations</h1>
            <p className="cim-page-subtitle">Manage and track all your wedding invitations</p>
          </div>
        </div>
        <button className="cim-btn-create" onClick={cimOpenCreate}>＋ Create Invitation</button>
      </div>

      {/* Stats */}
      <div className="cim-stats">
        <div className="cim-stat-card cim-stat-card--rose" style={{ animationDelay: '0s' }}>
          <div className="cim-stat-icon">💌</div>
          <div className="cim-stat-info">
            <div className="cim-stat-value">{cimStats.total}</div>
            <div className="cim-stat-label">Total Invitations</div>
          </div>
        </div>
        <div className="cim-stat-card cim-stat-card--purple" style={{ animationDelay: '0.07s' }}>
          <div className="cim-stat-icon">👥</div>
          <div className="cim-stat-info">
            <div className="cim-stat-value">{cimStats.totalG}</div>
            <div className="cim-stat-label">Total Guests</div>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div className="cim-action-bar">
        <div className="cim-search-wrap">
          <span className="cim-search-icon">🔍</span>
          <input type="text" className="cim-search-input"
            placeholder="Search by invitation ID, name..."
            value={cimSearch}
            onChange={e => { setCimSearch(e.target.value); setCimPage(1); }} />
        </div>
        <select className="cim-filter-select" value={cimFltEvent}
          onChange={e => { setCimFltEvent(e.target.value); setCimPage(1); }}>
          {CIM_EVENT_TYPES.map(e => <option key={e}>{e}</option>)}
        </select>
        <select className="cim-filter-select" value={cimFltStatus}
          onChange={e => { setCimFltStatus(e.target.value); setCimPage(1); }}>
          <option value="All">All Status</option>
          <option>Active</option><option>Draft</option>
          <option>Sent</option><option>Pending</option>
        </select>
        <div className="cim-action-bar-right">
          <button className="cim-btn-reset" onClick={() => { setCimSearch(''); setCimFltEvent('All'); setCimFltStatus('All'); setCimPage(1); }}>
            ↺ Reset
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="cim-table-card">
        <div className="cim-table-header">
          <h3 className="cim-table-title">💌 All Invitations</h3>
          <span className="cim-table-count">{cimFiltered.length} invitations</span>
        </div>

        <div className="cim-table-wrap">
          <table className="cim-table">
            <thead className="cim-thead">
              <tr>
                <th>Inv. ID</th>
                <th>Event</th>
                <th>Template</th>
                <th>Sent</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody className="cim-tbody">
              {cimPaginated.length > 0 ? cimPaginated.map((inv, i) => (
                <tr key={inv.id} style={{ animationDelay: `${i * 0.05}s` }}>
                  <td><span className="cim-inv-id">#{inv.id}</span></td>
                  <td>
                    <span className={cimEventBadgeClass(inv.eventType)}>
                      {cimEventEmoji(inv.eventType)} {inv.eventType}
                    </span>
                  </td>
                  <td>
                    <div className="cim-template-cell">
                      <div className={cimThumbClass(inv.templateTheme)}>
                        {CIM_TEMPLATES.find(t => t.id === inv.templateId)?.emoji || cimThemeEmoji(inv.templateTheme)}
                      </div>
                      <span className="cim-template-name">{inv.templateName}</span>
                    </div>
                  </td>
                  <td>
                    <div className="cim-count-cell">
                      👥 {inv.totalSent}
                    </div>
                  </td>
                  <td>
                    <span className={cimStatusBadgeClass(inv.status)}>
                      <span className="cim-status-dot" />
                      {inv.status}
                    </span>
                  </td>
                  <td>
                    <div className="cim-actions">
                      <button className="cim-btn-view"      onClick={() => { setCimViewInv(inv); setCimScreen('preview'); window.scrollTo(0,0); }}>👁 View</button>
                      <button className="cim-btn-edit-sm"   onClick={() => cimOpenEdit(inv)}>✏️ Edit</button>
                      <button className="cim-btn-delete-sm" onClick={() => setCimDeleteId(inv.id)}>🗑️</button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="6">
                    <div className="cim-empty">
                      <span className="cim-empty-icon">📭</span>
                      <p className="cim-empty-text">No invitations yet. Create your first one!</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="cim-pagination">
          <span className="cim-pagination-info">
            Showing <span>{cimFiltered.length === 0 ? 0 : (cimPage-1)*CIM_PER_PAGE+1}</span>–
            <span>{Math.min(cimPage*CIM_PER_PAGE, cimFiltered.length)}</span>
            {' '}of <span>{cimFiltered.length}</span> invitations
          </span>
          {cimTotalPages > 1 && (
            <div className="cim-page-btns">
              <button className="cim-page-btn" onClick={() => setCimPage(1)}           disabled={cimPage===1}>«</button>
              <button className="cim-page-btn" onClick={() => setCimPage(p=>p-1)}      disabled={cimPage===1}>‹</button>
              {Array.from({ length: cimTotalPages }, (_, i) => i+1).map(p => (
                <button key={p} className={`cim-page-btn ${cimPage===p?'cim-page-btn--active':''}`}
                  onClick={() => setCimPage(p)}>{p}</button>
              ))}
              <button className="cim-page-btn" onClick={() => setCimPage(p=>p+1)}      disabled={cimPage===cimTotalPages}>›</button>
              <button className="cim-page-btn" onClick={() => setCimPage(cimTotalPages)} disabled={cimPage===cimTotalPages}>»</button>
            </div>
          )}
        </div>
      </div>

      {/* Delete Modal */}
      {cimDeleteId && (
        <div className="cim-overlay" onClick={() => setCimDeleteId(null)}>
          <div className="cim-modal" onClick={e => e.stopPropagation()}>
            <div className="cim-modal-head">
              <p className="cim-modal-head-label">Confirm Action</p>
              <h2 className="cim-modal-head-title">🗑️ Delete Invitation</h2>
              <button className="cim-modal-close" onClick={() => setCimDeleteId(null)}>✕</button>
            </div>
            <div className="cim-delete-body">
              <span className="cim-delete-icon">🗑️</span>
              <h3 className="cim-delete-title">Are you sure?</h3>
              {cimDeleteTarget && <div className="cim-delete-name">#{cimDeleteTarget.id} — {cimDeleteTarget.eventType}</div>}
              <p className="cim-delete-text">This invitation and all guest data will be <strong>permanently deleted</strong>.</p>
              <div className="cim-delete-btns">
                <button className="cim-btn-no"          onClick={() => setCimDeleteId(null)}>No, Cancel</button>
                <button className="cim-btn-confirm-del" onClick={cimConfirmDelete}>🗑️ Yes, Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {cimToast && <CimToast msg={cimToast.msg} type={cimToast.type} />}
    </div>
    </CustomerLayout>
  );

  /* ═══════════════════════════════════════════
     SCREEN: CREATE (4 Steps)
  ═══════════════════════════════════════════ */
  if (cimScreen === 'create') return (
    <CustomerLayout>
    <div className="cim-wrapper">
      <div className="cim-create-page"> 

        <div className="cim-create-page-header">
          <button className="cim-btn-back" onClick={() => setCimScreen('list')}>← Back</button>
          <h2 className="cim-create-page-title">
            {cimEditId ? '✏️ Edit Invitation' : '💌 Create Invitation'}
          </h2>
        </div>

        <CimStepper />

        {/* ─── STEP 1: SELECT EVENT ─── */}
        {cimStep === 1 && (
          <div className="cim-step-card">
            <div className="cim-step-card-head">
              <div className="cim-step-card-num">1</div>
              <h3 className="cim-step-card-title">Select Your Event</h3>
            </div>
            <div className="cim-step-body">
              <p style={{ fontSize: 14, color: '#78716c', marginBottom: 20 }}>
                Choose the event for which you want to create invitations.
              </p>
              <div className="cim-event-options">
                {CIM_MOCK_EVENTS.map(ev => (
                  <div key={ev.id}
                    className={`cim-event-option ${cimForm.eventId === ev.id ? 'cim-event-option--selected' : ''}`}
                    onClick={() => setCimForm(f => ({ ...f, eventId: ev.id, eventType: ev.type, eventLabel: ev.label, templateId: '', templateName: '', templateTheme: '' }))}>
                    <span className="cim-event-option-icon">{ev.icon}</span>
                    <div className="cim-event-option-name">{ev.type}</div>
                    <div className="cim-event-option-id">#{ev.id}</div>
                  </div>
                ))}
              </div>
              {cimForm.eventId && (
                <div style={{ marginTop: 18, padding: '12px 16px', background: '#fff1f2', borderRadius: 10, border: '2px solid #fecdd3', fontSize: 13.5, fontWeight: 600, color: '#be123c' }}>
                  ✅ Selected: {cimForm.eventLabel}
                </div>
              )}
              <div className="cim-step-nav">
                <div />
                <div className="cim-step-nav-right">
                  <button className="cim-btn-next" onClick={cimGoNext}>Next → Step 2</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── STEP 2: SELECT TEMPLATE (Admin ke banaye hue real templates) ─── */}
        {cimStep === 2 && (
          <div className="cim-step-card">
            <div className="cim-step-card-head">
              <div className="cim-step-card-num">2</div>
              <h3 className="cim-step-card-title">Choose Invitation Template</h3>
            </div>
            <div className="cim-step-body">
              <p style={{ fontSize: 14, color: '#78716c', marginBottom: 20 }}>
                Select a beautiful template for your {cimForm.eventType} invitation.
              </p>

              {cimTemplatesLoading ? (
                <div style={{ textAlign: 'center', padding: 30, color: '#a8a29e', fontSize: 14 }}>
                  Loading templates...
                </div>
              ) : cimTemplates.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 30, color: '#a8a29e', fontSize: 14 }}>
                  No active templates found for {cimForm.eventType}. Please check back later.
                </div>
              ) : (
                <div className="cim-template-grid">
                  {cimTemplates.map(t => {
                    const themeKey = (t.theme || '').toLowerCase();
                    return (
                      <div key={t.id}
                        className={`cim-template-option ${cimForm.templateId === t.id ? 'cim-template-option--selected' : ''}`}
                        onClick={() => setCimForm(f => ({ ...f, templateId: t.id, templateName: t.name, templateTheme: themeKey }))}>
                        <div className={`cim-template-option-preview cim-template-option-preview--${themeKey}`}>
                          {t.bg_image ? (
                            <img
                              src={`${baseUrlForFile}/${t.bg_image}`}
                              alt={t.name}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }}
                            />
                          ) : (
                            cimThemeEmoji(themeKey)
                          )}
                          {cimForm.templateId === t.id && (
                            <span className="cim-template-option-selected-badge">✓</span>
                          )}
                        </div>
                        <div className="cim-template-option-info">
                          <div className="cim-template-option-name">{t.name}</div>
                          <div className="cim-template-option-theme">{t.theme} theme</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="cim-step-nav">
                <button className="cim-btn-prev" onClick={() => setCimStep(1)}>← Back</button>
                <div className="cim-step-nav-right">
                  <button className="cim-btn-next" onClick={cimGoNext}>Next → Step 3</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── STEP 3: FILL DETAILS (real event select + venue + message) ─── */}
        {cimStep === 3 && (
          <div className="cim-step-card">
            <div className="cim-step-card-head">
              <div className="cim-step-card-num">3</div>
              <h3 className="cim-step-card-title">Fill Invitation Details</h3>
            </div>
            <div className="cim-step-body">

              {/* Select Your Event (real events from My Events) */}
              <div className="cim-form-group cim-form-group--full" style={{ marginBottom: 20 }}>
                <label className="cim-form-label">Select Your Event <span>*</span></label>

                {cimRealEventsLoading ? (
                  <div style={{ padding: 20, color: '#a8a29e', fontSize: 14 }}>Loading your events...</div>
                ) : cimRealEvents.length === 0 ? (
                  <div style={{ padding: 20, color: '#a8a29e', fontSize: 14 }}>
                    No events found. Please create an event first from "My Events".
                  </div>
                ) : (
                  <div className="cim-event-options">
                    {cimRealEvents.map(ev => (
                      <div key={ev.id}
                        className={`cim-event-option ${cimForm.realEventId === ev.id ? 'cim-event-option--selected' : ''}`}
                        onClick={() => cimSelectRealEvent(ev)}>
                        <span className="cim-event-option-icon">{cimEventEmoji(ev.eventType)}</span>
                        <div className="cim-event-option-name">{ev.eventName}</div>
                        <div className="cim-event-option-id">{ev.brideName} &amp; {ev.groomName}</div>
                        <div style={{ fontSize: 12, color: '#a8a29e', marginTop: 4 }}>{ev.eventDate}</div>
                      </div>
                    ))}
                  </div>
                )}

                {cimForm.realEventId && (
                  <div style={{ marginTop: 14, padding: '12px 16px', background: '#fff1f2', borderRadius: 10, border: '2px solid #fecdd3', fontSize: 13.5, fontWeight: 600, color: '#be123c' }}>
                    ✅ Selected: {cimForm.brideName} &amp; {cimForm.groomName} — {cimForm.eventDate}
                  </div>
                )}
              </div>

              <div className="cim-form-grid">
                <div className="cim-form-group">
                  <label className="cim-form-label">Venue <span>*</span></label>
                  <input type="text" className="cim-form-input"
                    style={cimVenueError ? { borderColor: 'red' } : undefined}
                    placeholder="e.g. Pearl Continental, Karachi" value={cimForm.venue}
                    onChange={e => {
                      const val = e.target.value;
                      setCimForm(f => ({ ...f, venue: val }));
                      setCimVenueError(val.trim() ? regexChecker('venue', val) : 'Venue is required');
                    }} />
                  {cimVenueError && (
                    <p style={{ color: 'red', fontSize: '13px', marginTop: 4 }}>{cimVenueError}</p>
                  )}
                </div>
                <div className="cim-form-group cim-form-group--full">
                  <label className="cim-form-label">Custom Message</label>
                  <textarea className="cim-form-textarea"
                    placeholder="Add a personal message for your guests..."
                    value={cimForm.customMessage}
                    onChange={e => setCimForm(f => ({ ...f, customMessage: e.target.value }))} />
                </div>
              </div>

              {/* Mini Preview */}
              {cimForm.brideName && cimForm.groomName && (
                <div style={{ marginTop: 8, marginBottom: 8 }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: '#a8a29e', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 10 }}>
                    Live Preview
                  </p>
                  <CimInvCard inv={cimForm} compact />
                </div>
              )}

              <div className="cim-step-nav">
                <button className="cim-btn-prev" onClick={() => setCimStep(2)}>← Back</button>
                <div className="cim-step-nav-right">
                  <button className="cim-btn-next" onClick={cimGoNext}>Next → Guest List</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── STEP 4: GUEST LIST ─── */}
        {cimStep === 4 && (
          <div className="cim-step-card">
            <div className="cim-step-card-head">
              <div className="cim-step-card-num">4</div>
              <h3 className="cim-step-card-title">Guest List</h3>
            </div>
            <div className="cim-step-body">

              <div className="cim-guest-add-options">
                <label className="cim-guest-add-btn">
                  📎 Upload CSV
                  <input type="file" accept=".csv" style={{ display: 'none' }} onChange={cimHandleCsv} />
                </label>
                <button className="cim-guest-add-btn cim-guest-add-btn--primary" onClick={cimAddGuest}>
                  ＋ Add Guest Manually
                </button>
                <span style={{ fontSize: 13, color: '#78716c', alignSelf: 'center' }}>
                  {cimGuests.filter(g => g.name.trim()).length} guests added
                </span>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table className="cim-guest-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Guest Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Remove</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cimGuests.map((g, i) => {
                      const gErr = cimGuestErrors[g.id] || {};
                      return (
                        <tr key={g.id}>
                          <td style={{ color: '#a8a29e', fontWeight: 700, width: 36 }}>{i+1}</td>
                          <td>
                            <input placeholder="Guest name" value={g.name}
                              onChange={e => cimUpdateGuest(g.id, 'name', e.target.value)} />
                          </td>
                          <td>
                            <input placeholder="Email" value={g.email}
                              style={gErr.email ? { borderColor: 'red' } : undefined}
                              onChange={e => cimUpdateGuest(g.id, 'email', e.target.value)} />
                            {gErr.email && (
                              <p style={{ color: 'red', fontSize: '12px', margin: '2px 0 0' }}>{gErr.email}</p>
                            )}
                          </td>
                          <td>
                            <input placeholder="Phone" value={g.phone}
                              style={gErr.phone ? { borderColor: 'red' } : undefined}
                              onChange={e => cimUpdateGuest(g.id, 'phone', e.target.value)} />
                            {gErr.phone && (
                              <p style={{ color: 'red', fontSize: '12px', margin: '2px 0 0' }}>{gErr.phone}</p>
                            )}
                          </td>
                          <td>
                            <button className="cim-guest-remove" onClick={() => cimRemoveGuest(g.id)}>✕</button>
                          </td>
                        </tr>
                      );
                    })}
                    {cimGuests.length === 0 && (
                      <tr>
                        <td colSpan="5" style={{ textAlign: 'center', padding: 24, color: '#a8a29e', fontSize: 14 }}>
                          No guests added yet. Upload CSV or add manually.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="cim-step-nav">
                <button className="cim-btn-prev" onClick={() => setCimStep(3)}>← Back</button>
                <div className="cim-step-nav-right">
                  <button className="cim-btn-preview" onClick={cimGoToPreview}>🔍 Preview</button>
                  <button className="cim-btn-draft"   onClick={cimSaveDraft}>📝 Save Draft</button>
                  <button className="cim-btn-send"    onClick={() => cimSaveInvitation('Sent')}>📤 Send Now</button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
      {cimToast && <CimToast msg={cimToast.msg} type={cimToast.type} />}
    </div>
    </CustomerLayout>
  );

  /* ═══════════════════════════════════════════
     SCREEN: PREVIEW
  ═══════════════════════════════════════════ */
  if (cimScreen === 'preview' && cimViewInv) return (
    <CustomerLayout>
    <div className="cim-wrapper">
      <div className="cim-preview-page">

        <div className="cim-create-page-header">
          <button className="cim-btn-back" onClick={() => setCimScreen('list')}>← Back to List</button>
          <h2 className="cim-create-page-title">💌 Invitation Preview</h2>
        </div>

        <div className="cim-preview-layout">

          {/* Left: Details */}
          <div className="cim-preview-detail-card">
            <div className="cim-preview-detail-head">
              <div className="cim-preview-detail-title">
                #{cimViewInv.id} — {cimViewInv.eventType} Invitation
              </div>
              <div className="cim-preview-detail-sub">
                Created {cimViewInv.createdAt} · Template: {cimViewInv.templateName}
              </div>
            </div>
            <div className="cim-preview-detail-body">
              {[
                { icon: '💍', label: 'Event',        value: cimViewInv.eventLabel },
                { icon: '👰', label: 'Bride',         value: cimViewInv.brideName  },
                { icon: '🤵', label: 'Groom',         value: cimViewInv.groomName  },
                { icon: '📅', label: 'Date',          value: cimViewInv.eventDate  },
                { icon: '📍', label: 'Venue',         value: cimViewInv.venue      },
                { icon: '💬', label: 'Message',       value: cimViewInv.customMessage || '—' },
                { icon: '👥', label: 'Total Guests',  value: `${cimViewInv.totalSent} guests` },
              ].map((row, i) => (
                <div className="cim-detail-row" key={i}>
                  <div className="cim-detail-icon-box">{row.icon}</div>
                  <div>
                    <div className="cim-detail-label">{row.label}</div>
                    <div className="cim-detail-value">{row.value}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="cim-preview-actions">
              <button className="cim-btn-edit-lg" onClick={() => {
                const inv = cimData.find(i => i.id === cimViewInv.id);
                if (inv) cimOpenEdit(inv);
                else { setCimScreen('create'); setCimStep(1); }
              }}>✏️ Edit</button>
              <button className="cim-btn-confirm-send" onClick={cimSend}>📤 Confirm &amp; Send</button>
            </div>
          </div>

          {/* Right: Card Preview */}
          <div className="cim-preview-card-wrap">
            <div className="cim-preview-card-box">
              <div className="cim-preview-card-box-head">
                <h3 className="cim-preview-card-box-title">Card Preview</h3>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#be123c' }}>
                  {cimViewInv.templateName}
                </span>
              </div>
              <div className="cim-preview-canvas-box">
                <CimInvCard inv={cimViewInv} />
              </div>
            </div>
          </div>

        </div>
      </div>
      {cimToast && <CimToast msg={cimToast.msg} type={cimToast.type} />}
    </div>
    </CustomerLayout>
  );

  return null;
};

export default Invitations;